<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import {
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
  MANAGE_ALL_CONVERSATION_PERMISSIONS,
  CONVERSATION_UNASSIGNED_PERMISSIONS,
  CONVERSATION_PARTICIPATING_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import { isSourceChannel } from 'dashboard/helper/sourcePlatforms';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const name = ref('');
const description = ref('');
const selectedPermissions = ref([]);
const restrictSources = ref(false);
const selectedInboxIds = ref([]);

const allInboxes = useMapGetter('inboxes/getInboxes');

// Marketplaces and social sources first, then the rest; alphabetical inside.
const inboxes = computed(() =>
  [...allInboxes.value].sort(
    (a, b) =>
      Number(!isSourceChannel(a)) - Number(!isSourceChannel(b)) ||
      a.name.localeCompare(b.name)
  )
);

const toggleInbox = id => {
  selectedInboxIds.value = selectedInboxIds.value.includes(id)
    ? selectedInboxIds.value.filter(inboxId => inboxId !== id)
    : [...selectedInboxIds.value, id];
};

const sourcesError = computed(
  () => restrictSources.value && !selectedInboxIds.value.length
);

const nameInput = ref(null);

const addCustomRole = reactive({
  showLoading: false,
  message: '',
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  description: { required },
  selectedPermissions: { required, minLength: minLength(1) },
}));

const v$ = useVuelidate(rules, { name, description, selectedPermissions });

const resetForm = () => {
  name.value = '';
  description.value = '';
  selectedPermissions.value = [];
  restrictSources.value = false;
  selectedInboxIds.value = [];
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedRole.name || '';
  description.value = props.selectedRole.description || '';
  selectedPermissions.value = props.selectedRole.permissions || [];
  selectedInboxIds.value = [...(props.selectedRole.inbox_ids || [])];
  restrictSources.value = selectedInboxIds.value.length > 0;
};

watch(
  selectedPermissions,
  (newValue, oldValue) => {
    // Check if manage all conversation permission is added or removed
    const hasAddedManageAllConversation =
      newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);
    const hasRemovedManageAllConversation =
      oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);

    if (hasAddedManageAllConversation) {
      // If manage all conversation permission is added,
      // then add unassigned and participating permissions automatically
      selectedPermissions.value = [
        ...new Set([
          ...selectedPermissions.value,
          CONVERSATION_UNASSIGNED_PERMISSIONS,
          CONVERSATION_PARTICIPATING_PERMISSIONS,
        ]),
      ];
    } else if (hasRemovedManageAllConversation) {
      // If manage all conversation permission is removed,
      // then only remove manage all conversation permission
      selectedPermissions.value = selectedPermissions.value.filter(
        p => p !== MANAGE_ALL_CONVERSATION_PERMISSIONS
      );
    }
  },
  { deep: true }
);

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
  }
  // Focus the name input when mounted
  nameInput.value?.focus();
});

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `CUSTOM_ROLE.EDIT.${base}`
    : `CUSTOM_ROLE.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('SUBMIT')));

const handleCustomRole = async () => {
  v$.value.$touch();
  if (v$.value.$invalid || sourcesError.value) return;

  addCustomRole.showLoading = true;
  try {
    const roleData = {
      name: name.value,
      description: description.value,
      permissions: selectedPermissions.value,
      inbox_ids: restrictSources.value ? selectedInboxIds.value : [],
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...roleData,
      });
      useAlert(t('CUSTOM_ROLE.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customRole/createCustomRole', roleData);
      useAlert(t('CUSTOM_ROLE.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`CUSTOM_ROLE.FORM.API.ERROR_MESSAGE`);
    useAlert(errorMessage);
  } finally {
    addCustomRole.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || sourcesError.value || addCustomRole.showLoading
);
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <form class="flex flex-col w-full" @submit.prevent="handleCustomRole">
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CUSTOM_ROLE.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model.trim="name"
            type="text"
            :placeholder="$t('CUSTOM_ROLE.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.description.$error }">
          {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL') }}

          <textarea
            v-model="description"
            :rows="3"
            :placeholder="$t('CUSTOM_ROLE.FORM.DESCRIPTION.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ 'text-n-ruby-9': v$.selectedPermissions.$error }">
          {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL') }}
        </label>
        <div class="flex flex-col gap-2.5 mb-4 mt-2">
          <div
            v-for="permission in AVAILABLE_CUSTOM_ROLE_PERMISSIONS"
            :key="permission"
            class="flex items-center"
          >
            <input
              :id="permission"
              v-model="selectedPermissions"
              type="checkbox"
              :value="permission"
              name="permissions"
              class="ltr:mr-2 rtl:ml-2"
            />
            <label :for="permission" class="text-sm font-normal">
              {{ $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`) }}
            </label>
          </div>
        </div>
      </div>

      <div class="w-full mb-4">
        <label>{{ $t('CUSTOM_ROLE.FORM.SOURCES.LABEL') }}</label>
        <div
          class="inline-flex p-0.5 mt-2 rounded-lg bg-n-alpha-2 outline outline-1 outline-n-weak"
        >
          <button
            v-for="option in [false, true]"
            :key="String(option)"
            type="button"
            class="h-7 px-3 rounded-md text-sm transition-colors"
            :class="
              restrictSources === option
                ? 'bg-n-solid-1 text-n-slate-12 shadow-sm font-medium'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="restrictSources = option"
          >
            {{
              option
                ? $t('CUSTOM_ROLE.FORM.SOURCES.SELECTED')
                : $t('CUSTOM_ROLE.FORM.SOURCES.ALL')
            }}
          </button>
        </div>
        <p class="mt-2 mb-0 text-sm text-n-slate-11">
          {{
            restrictSources
              ? $t('CUSTOM_ROLE.FORM.SOURCES.SELECTED_HINT')
              : $t('CUSTOM_ROLE.FORM.SOURCES.ALL_HINT')
          }}
        </p>
        <template v-if="restrictSources">
          <div
            class="grid grid-cols-2 gap-1.5 mt-3 p-1.5 max-h-64 overflow-y-auto rounded-lg outline outline-1 outline-n-weak"
          >
            <button
              v-for="inbox in inboxes"
              :key="inbox.id"
              type="button"
              class="flex items-center gap-2 min-w-0 h-9 px-2 rounded-md text-left text-sm transition-colors"
              :class="
                selectedInboxIds.includes(inbox.id)
                  ? 'bg-n-brand/10 text-n-slate-12'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="toggleInbox(inbox.id)"
            >
              <span
                class="grid place-items-center size-4 flex-shrink-0 rounded border"
                :class="
                  selectedInboxIds.includes(inbox.id)
                    ? 'bg-n-brand border-n-brand text-white'
                    : 'border-n-slate-6'
                "
              >
                <Icon
                  v-if="selectedInboxIds.includes(inbox.id)"
                  icon="i-lucide-check"
                  class="size-3"
                />
              </span>
              <ChannelIcon
                :inbox="inbox"
                use-brand-icon
                class="size-4 flex-shrink-0"
              />
              <span class="truncate">{{ inbox.name }}</span>
            </button>
          </div>
          <p
            class="mt-2 mb-0 text-sm"
            :class="sourcesError ? 'text-n-ruby-11' : 'text-n-slate-11'"
          >
            {{
              sourcesError
                ? $t('CUSTOM_ROLE.FORM.SOURCES.ERROR')
                : $t('CUSTOM_ROLE.FORM.SOURCES.SELECTED_COUNT', {
                    n: selectedInboxIds.length,
                  })
            }}
          </p>
        </template>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="addCustomRole.showLoading"
        />
      </div>
    </form>
  </div>
</template>
